import { useData } from "@hooks/useData";
import http from "@core/fetch";
import { ID } from "@datassential/platform-ui-lib";
import dayjs from "dayjs";
import { PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";

export interface IMacItemObsolete {
    id: ID;
    documentType: ID;
    publicationFamily: string;
    title: string;
    description: {
        preview: string;
        additional: string;
    };
    countryCodeStr: string;
    itemsCount: number;
    shareLink: string;
    dateStr: string;
    dateSort: Date;
    downloadLinks: Array<{
        fileExtension: string;
        url: string;
    }>;
    thumbnailUrl: string;
}

type ApiMacItemModel = {
    id: ID;
    dateTime: string
    description: string;
    documentType: ID;
    publicationFamily: string;
    downloadLinks: Array<{
        fileExtension: "ppt" | "jpeg";
        linkToDownload: string;
        title: string;
    }>;
    generateLink: string;
    modifiedDate: string;
    shareLinkString: string;
    nonUS: boolean;
    title: "Canadian new kombucha MAC";
    thumnailUrl: string;
};

function getMacWords(rawDescription: string): string[] {
    const rawList = rawDescription.split('<');

    return rawList.map((elem) => elem.split('|')[0].trim());
}

function getMacDescription(words: string[]) {
    const SHORT_WORDS_COUNT = 2;
    
    return {
        preview: words.length > 0 ? `includes ${words.slice(0, SHORT_WORDS_COUNT).join(', ')}` : '',
        additional: words.slice(SHORT_WORDS_COUNT).join(', ')
    };
}

type ApiModel = {
    permissionLevel?: number;
    hasData?: boolean;
    data?: Array<{ items: ApiMacItemModel[] }>;
};

const FAKE_MAC_TITLES = [
    { title: 'BBQ Flavors', words: ['Tandoor', 'Asado', 'Souvlaki', 'Lemon Grass', 'Curry'] },
    { title: 'Burger Ingredients', words: ['Wagyu', 'Tzatziki', 'Manchego', 'Kale', 'Fontina'] },
    { title: 'Breakfast Dishes', words: ['Shakshuka', 'Chilaquiles', 'Migas', 'Avocado Toast'] },
    { title: 'Cocktail Flavors', words: ['Yuzu', 'Elderflower', 'Hibiscus', 'Lavender'] },
    { title: 'Dessert Varieties', words: ['Mochi', 'Macaron', 'Gelato', 'Churro', 'Beignet'] },
    { title: 'Asian Cuisine', words: ['Bibimbap', 'Pho', 'Ramen', 'Bao', 'Poke'] },
    { title: 'Mexican Cuisine', words: ['Birria', 'Elote', 'Tamale', 'Mole', 'Pastor'] },
    { title: 'Comfort Foods', words: ['Poutine', 'Ramen', 'Mac and Cheese', 'Fried Chicken'] },
    { title: 'BBQ Flavors', words: ['Tandoor', 'Asado', 'Souvlaki', 'Lemon Grass', 'Curry'] },
    { title: 'Burger Ingredients', words: ['Wagyu', 'Tzatziki', 'Manchego', 'Kale', 'Fontina'] },
    { title: 'Breakfast Dishes', words: ['Shakshuka', 'Chilaquiles', 'Migas', 'Avocado Toast'] },
    { title: 'Cocktail Flavors', words: ['Yuzu', 'Elderflower', 'Hibiscus', 'Lavender'] },
    { title: 'Dessert Varieties', words: ['Mochi', 'Macaron', 'Gelato', 'Churro', 'Beignet'] },
    { title: 'Asian Cuisine', words: ['Bibimbap', 'Pho', 'Ramen', 'Bao', 'Poke'] },
    { title: 'Mexican Cuisine', words: ['Birria', 'Elote', 'Tamale', 'Mole', 'Pastor'] },
    { title: 'Comfort Foods', words: ['Poutine', 'Ramen', 'Mac and Cheese', 'Fried Chicken'] },
];

function getFakeMacItem(index: number): IMacItemObsolete {
    const { title, words } = FAKE_MAC_TITLES[index % FAKE_MAC_TITLES.length];
    return {
        id: index + 1,
        documentType: 3,
        publicationFamily: '',
        title,
        description: getMacDescription(words),
        countryCodeStr: 'USA',
        itemsCount: words.length,
        shareLink: '',
        dateStr: dayjs().subtract(index, 'month').format('MM/DD/YYYY'),
        dateSort: dayjs().subtract(index, 'month').toDate(),
        downloadLinks: [],
        thumbnailUrl: '',
    };
}

function getFakeMacItems(): IMacItemObsolete[] {
    return FAKE_MAC_TITLES.map((_, i) => getFakeMacItem(i));
}

function mapMacItems(apiItems: ApiMacItemModel[]): IMacItemObsolete[] {
    return apiItems.map((item) => {
        const words = getMacWords(item.description);
        return ({
            id: item.id,
            title: item.title,
            description: getMacDescription(words),
            countryCodeStr: item.nonUS ? 'CAN' : 'USA',
            itemsCount: words.length,
            shareLink: item.shareLinkString,
            downloadLinks: [
                { fileExtension: 'PPT', url: item.downloadLinks[1]?.linkToDownload ?? '' },
                { fileExtension: 'PNG', url: item.downloadLinks[3]?.linkToDownload ?? '' },
            ],
            dateStr: dayjs(item.modifiedDate).format('MM/DD/YYYY'),
            dateSort: new Date(item.modifiedDate),
            documentType: item.documentType,
            publicationFamily: item.publicationFamily,
            thumbnailUrl: item.thumnailUrl,
        }) as IMacItemObsolete;
    });
}

export async function getMacsListObsolete(): Promise<{ items: IMacItemObsolete[]; permissionLevel: PermissionLevelEnum; hasData: boolean }> {
    const response = await http.get('documents', {
        doctype: 'Mac',
        skip: 0,
        take: 1000,
        word: '',
        t: Date.now(),
    }) as ApiModel;

    try {
        const permissionLevel = PermissionSection.getPermissionLevel(response.permissionLevel as 0 | 1 | 3);
        const hasData = response.hasData ?? true;
        const items = permissionLevel === PermissionLevelEnum.Full
            ? mapMacItems(response.data?.[0]?.items ?? [])
            : getFakeMacItems();

        return { items, permissionLevel, hasData };
    }
    catch (e) {
        console.error(e);
        return { items: [], permissionLevel: PermissionLevelEnum.Full, hasData: true };
    }
}

export function useMacsListObsolete () {
    return useData({ items: [], permissionLevel: PermissionLevelEnum.Full, hasData: true }, getMacsListObsolete);
}